<script setup lang="ts">
import { computed, ref, useAttrs } from 'vue';
import type { DataTableMethods, DataTablePageEvent } from 'primevue/datatable';
import UiCell from './UiCell.vue';
import type { TableColumnConfig } from './UiCell.vue';
import UiTableSkeleton from './UiTableSkeleton.vue';
import { DEFAULT_PAGINATOR_TEMPLATE, DEFAULT_PAGE_REPORT_TEMPLATE } from './table-constants';

defineOptions({ inheritAttrs: false });

const emit = defineEmits<{
  page: [event: DataTablePageEvent];
}>();

const {
  columns = [],
  selectable = false,
  loading = false,
  loadingColumns = 8,
  loadingCircleColumns = [0],
  dataKey = 'id',
  rows = 20,
  paginator = true,
  sortOrder = -1,
  paginatorTemplate = DEFAULT_PAGINATOR_TEMPLATE,
  currentPageReportTemplate = DEFAULT_PAGE_REPORT_TEMPLATE,
  showFirstLastPageLinks = true,
  fill = false,
} = defineProps<{
  columns?: TableColumnConfig[];
  selectable?: boolean;
  loading?: boolean;
  loadingColumns?: number;
  loadingCircleColumns?: number[];
  dataKey?: string;
  rows?: number;
  paginator?: boolean;
  sortOrder?: number;
  paginatorTemplate?: string;
  currentPageReportTemplate?: string;
  showFirstLastPageLinks?: boolean;
  fill?: boolean;
}>();

const attrs = useAttrs();

const resolvedPaginatorTemplate = computed(() => {
  if (showFirstLastPageLinks) return paginatorTemplate;

  return paginatorTemplate
    .split(' ')
    .filter((item) => item !== 'FirstPageLink' && item !== 'LastPageLink')
    .join(' ');
});

const tableProps = computed(() => ({
  dataKey,
  rows,
  paginator,
  sortOrder,
  paginatorTemplate: resolvedPaginatorTemplate.value,
  currentPageReportTemplate,
  ...attrs,
}));

const colProps = (col: TableColumnConfig) => {
  const { key: _key, bodySlot: _bodySlot, cellProps: _cellProps, ...rest } = col;
  return rest;
};

type DataTableInstance = DataTableMethods & { $el: HTMLElement };

const dtRef = ref<DataTableInstance>();

const forwardPageEvent = (event: DataTablePageEvent) => {
  dtRef.value?.$el
    .querySelector<HTMLElement>('.p-datatable-table-container')
    ?.scrollTo({ top: 0, behavior: 'instant' });
  emit('page', event);
};

defineExpose({ dt: dtRef });
</script>

<template>
  <UiTableSkeleton
    v-if="loading"
    :columns-count="loadingColumns"
    :circle-columns="loadingCircleColumns"
  />
  <DataTable
    v-else
    ref="dtRef"
    v-bind="tableProps"
    :class="{ 'ui-data-table--fill': fill }"
    @page="forwardPageEvent"
  >
    <Column v-if="selectable" selectionMode="multiple" headerStyle="width: 3rem" />
    <slot name="columnsBefore" />
    <Column v-for="col in columns" :key="col.key" v-bind="colProps(col)">
      <template #body="{ data }">
        <UiCell v-if="col.bodySlot" :name="col.bodySlot" :row="data" v-bind="col.cellProps" />
        <span v-else>{{ col.field ? data[col.field] : '' }}</span>
      </template>
    </Column>
    <slot name="columnsAfter" />
    <template v-if="$slots.paginatorstart" #paginatorstart>
      <slot name="paginatorstart" />
    </template>
    <template v-if="$slots.paginatorend" #paginatorend>
      <slot name="paginatorend" />
    </template>
  </DataTable>
</template>

<style lang="scss" scoped>
.ui-data-table--fill {
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;

  :deep(.p-datatable-table-container),
  :deep(.p-datatable-wrapper) {
    flex: 1;
    min-height: 0;
    overflow: auto;
  }

  :deep(.p-datatable-paginator-bottom),
  :deep(.p-paginator) {
    margin-top: auto;
  }
}

:deep(.p-datatable-paginator-bottom) {
  flex-shrink: 0;
  border: none;
}

:deep(.p-paginator) {
  padding: 7px 0;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
}

:deep(.p-paginator-content) {
  grid-column: 2;
  justify-self: center;
}

:deep(.p-paginator-current) {
  display: inline-block;
  min-width: 24ch;
  text-align: center;
}

:deep(.p-paginator-content-end) {
  grid-column: 3;
  justify-self: end;
}
</style>
